<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <v-row v-if="hasOwnFilters" align="start">
      <v-col cols="8" lg="auto" class="d-flex align-center">
        <report-meals-select-all-companies
          v-model="selectedCompanies"
        ></report-meals-select-all-companies>
      </v-col>
      <v-col cols="4" lg="auto" class="d-flex align-center">
        <shift-picker v-model="selectedShifts" multiple />
      </v-col>
    </v-row>
    <v-card
      flat
      class="company-card mb-6"
      outlined
      v-for="company in companies"
      :key="company.name"
    >
      <v-card-title>
        <v-row align="center" no-gutters>
          <v-col>
            <div class="text-center">
              <!-- Company Name -->
              <div class="company-header mb-4">
                <h2 class="company-title">
                  {{ toLatin(company.name) }}
                </h2>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-0">
        <v-data-table
          :headers="getMobileHeaders(headers)"
          :items="customSearch(company.meals, headers, filters.search)"
          class="ordera-box-shadow striped-table"
          hide-default-footer
          disable-pagination
          mobile-breakpoint="0"
        >
          <template v-slot:item.name="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  {{ item.name }}
                </span>
              </template>
              <span>{{ item.id }}</span>
            </v-tooltip>
          </template>
          <template #body.append>
            <tr class="font-weight-bold totals-row">
              <td v-if="!device.isMobile"></td>
              <td>{{ $t("total") }}</td>
              <td v-for="shift in shiftsWithData" :key="shift" class="text-right">
                {{ formatShiftTotal(company.totals, shift) }}
              </td>
              <td class="text-right">
                {{ company.totals.totalPayed }}/{{ company.totals.total }}
              </td>
            </tr>
          </template>
          <!-- Dynamic shift templates -->
          <template
            v-for="shift in shiftsWithData"
            v-slot:[`item.${getShiftKey(shift)}`]="{ item }"
          >
            {{ formatShiftQuantity(item[getShiftKey(shift)]) }}
          </template>
          <template v-slot:item.total="{ item }">
            {{ item.totalPayed }}/{{ item.total }}
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import reportService from "@/services/report";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import ReportMealsPackagesAddMeal from "@/components/report/meals/dialogs/ReportMealsPackagesAddMeal.vue";
import ReportMealsSelectAllCompanies from "@/components/report/meals/fields/ReportMealsSelectAllCompanies.vue";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasMobileHeaders from "@/mixins/report/hasMobileHeaders";
import { SHIFTS, getShiftKey } from "@/constants/shifts";

export default {
  name: "ReportMealsTabTakenMeals",
  mixins: [hasPrinting, hasReportPrinting, hasExcel, hasSnackbar, hasMobileHeaders],
  components: {
    ReportMealsSelectAllCompanies,
    ReportMealsPackagesAddMeal,
  },
  data() {
    return {
      report: [],
      selectedShifts: SHIFTS.map((s) => s.id),
      selectedCompanies: [],
    };
  },
  methods: {
    getShiftKey,
    formatShiftQuantity(shiftData) {
      const { orders = 0, taken = 0 } = shiftData || {};
      return orders === 0 ? "0/0" : `${taken}/${orders}`;
    },

    formatShiftTotal(totals, shiftId) {
      const key = this.getShiftKey(shiftId);
      const taken = totals[key + "_payed"] || 0;
      const orders = totals[key] || 0;
      return `${taken}/${orders}`;
    },

    prepareDataForExport(companies) {
      return companies.map((company) => {
        const items = company.meals.map((meal) => {
          const row = {
            num: meal.num,
            name: this.toLatin(meal.name),
            total: `${meal.totalPayed}/${meal.total}`,
          };

          this.shiftsWithData.forEach((shiftId) => {
            const key = this.getShiftKey(shiftId);
            row[key] = this.formatShiftQuantity(meal[key]);
          });

          return row;
        });

        // Add totals row
        const totalsRow = { num: "", name: this.$t("totalU") };
        this.shiftsWithData.forEach((shiftId) => {
          const key = this.getShiftKey(shiftId);
          totalsRow[key] = this.formatShiftTotal(company.totals, shiftId);
        });
        totalsRow.total = `${company.totals.totalPayed}/${company.totals.total}`;
        items.push(totalsRow);

        return {
          subtitle: this.generateTableSubtitle(),
          title: this.toLatin(company.name),
          items,
        };
      });
    },

    generateTableSubtitle() {
      const fromDate = this.filters.dates.from;
      const toDate = this.filters.dates.to;

      if (fromDate === toDate) {
        return fromDate;
      } else {
        return `${fromDate} - ${toDate}`;
      }
    },

    async loadMealsForRestaurantCompanies() {
      const params = {
        date: this.filters.dates.from,
        date2: this.filters.dates.to,
        company: this.selectedCompanies.map((company) => company.id).join(","),
      };

      try {
        this.report = await reportService.getMealsForRestaurantCompanies(
          this.$store.state.restaurant.id,
          params
        );
      } catch (error) {
        this.report = [];
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      }
    },
  },
  computed: {
    /** Stari izbori firme/smena iznad tabele; nova ljuska (računar) ih ima u zajedničkoj traci, pa ih isključuje. */
    hasOwnFilters() {
      return true;
    },
    loading() {
      return !this.report || this.report.length === 0;
    },

    companies() {
      if (!this.report?.length) return [];

      // If all shifts selected, return data as-is from mapper
      const allShiftsSelected = this.selectedShifts.length === SHIFTS.length;

      return this.report.map((company) => {
        if (allShiftsSelected) {
          // Merge with company details and return
          const companyDetails = this.selectedCompanies.find(
            (c) => c.name === company.name || c.id === company.id
          );
          return { ...company, ...companyDetails };
        }

        // Recalculate totals for filtered shifts
        const meals = company.meals.map((meal) => {
          let total = 0;
          let totalPayed = 0;

          SHIFTS.forEach((shift) => {
            if (this.selectedShifts.includes(shift.id)) {
              total += meal[shift.key].orders;
              totalPayed += meal[shift.key].taken;
            }
          });

          return { ...meal, total, totalPayed };
        });

        const totals = { total: 0, totalPayed: 0 };
        SHIFTS.forEach((shift) => {
          totals[shift.key] = 0;
          totals[shift.key + "_payed"] = 0;

          if (this.selectedShifts.includes(shift.id)) {
            meals.forEach((meal) => {
              totals[shift.key] += meal[shift.key].orders;
              totals[shift.key + "_payed"] += meal[shift.key].taken;
            });
          }
        });

        meals.forEach((meal) => {
          totals.total += meal.total;
          totals.totalPayed += meal.totalPayed;
        });

        const companyDetails = this.selectedCompanies.find(
          (c) => c.name === company.name || c.id === company.id
        );

        return {
          ...company,
          ...companyDetails,
          meals,
          totals,
        };
      });
    },

    shiftsWithData() {
      return this.selectedShifts.sort((a, b) => a - b);
    },
    headers() {
      const baseHeaders = [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("meal"), value: "name" },
      ];

      // Show headers only for selected shifts
      const shiftHeaders = SHIFTS.filter((shift) =>
        this.selectedShifts.includes(shift.id)
      ).map((shift) => ({
        text: this.$t(shift.label),
        value: shift.key,
        align: "right",
      }));

      const totalHeader = [{ text: this.$t("total"), value: "total", align: "right" }];

      return [...baseHeaders, ...shiftHeaders, ...totalHeader];
    },
    filters() {
      return this.$store.state.report.filters;
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.loadMealsForRestaurantCompanies();
      },
      deep: true,
    },
    selectedCompanies: {
      handler() {
        this.loadMealsForRestaurantCompanies();
      },
      immediate: true,
      deep: true,
    },
    isPrinting() {
      if (this.isPrinting) {
        const exportData = this.prepareDataForExport(this.companies);
        this.printDocument({
          tables: exportData.map((data, index) => ({
            ...data,
            headers: this.headers,
            title: data.title + " - " + this.subtitle,
            subtitle: [this.$t("report"), this.$t("pak")].join(": "),
            caption:
              this.$t("report_creation_datetime") +
              ": " +
              this.localeDate(new Date(), {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
              }),
          })),
          hideDatetime: true,
          additionalStyles: `
            .print-table {
              page-break-after: always;
            }
            .print-table:last-child {
              page-break-after: auto;
            }
          `,
        });
      }
    },

    isExcelDownloading() {
      if (this.isExcelDownloading) {
        const exportData = this.prepareDataForExport(this.companies);
        this.downloadExcel({
          fileName: [this.$t("report"), this.$t("pak")].join(" ") + ".xlsx",
          sheets: exportData.map((data) => ({
            name: data.title,
            headers: this.headers,
            items: data.items,
            title: data.title,
            subtitle: this.subtitle,
          })),
        });
      }
    },
  },
};
</script>

<style scoped>
.company-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  background: linear-gradient(145deg, #ffffff 0%, rgba(0, 0, 0, 0.02) 100%);
}

.company-card:hover {
  border-color: rgba(25, 118, 210, 0.2);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
}

.company-header {
  position: relative;
}

.company-title {
  font-size: 1.75rem;
  font-weight: 500;
  color: #2c3e50;
  margin: 0;
  letter-spacing: -0.5px;
}

@media (max-width: 768px) {
  .company-details {
    flex-direction: column;
    gap: 8px;
  }

  .company-title {
    font-size: 1.5rem;
  }
}

@media (max-width: 600px) {
  .company-card {
    border-radius: 8px;
  }

  .company-title {
    font-size: 1.3rem;
  }
}
</style>
